import * as React from 'react'

import { Trash2Icon } from 'lucide-react'
import { inject, observer } from 'mobx-react'

import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import WorkflowType from '~/client/src/shared/models/WorkflowType'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'

import RestrictionEditor from './RestrictionEditor'

// localization: translated

interface IProps {
  fieldId: string
  workflowTypes: string[]
  onChange: (fieldId: string, workflowTypes: string[]) => void
  workflowTypesStore?: WorkflowTypesStore
}

const ICON_SIZE = 14

const RestrictionButton: React.FC<IProps> = ({
  fieldId,
  workflowTypes,
  onChange,
  workflowTypesStore,
}) => {
  const actualTypes = React.useMemo((): WorkflowType[] => {
    return (
      workflowTypes?.reduce((list, type) => {
        const permitType = workflowTypesStore.getLastUpdatedTypeByType(type)
        if (permitType) {
          list.push(permitType)
        }
        return list
      }, [] as WorkflowType[]) || []
    )
  }, [workflowTypes])

  const title = React.useMemo((): string => {
    if (!actualTypes.length) {
      return Localization.translator.allWorkflow_s
    }
    return Localization.translator.xWorkflows(actualTypes.length)
  }, [actualTypes])

  const handleChange = React.useCallback(
    (selectedTypes: string[]) => {
      onChange(fieldId, selectedTypes)
    },
    [fieldId, onChange],
  )

  const [isOpen, setIsOpen] = React.useState(false)
  const close = React.useCallback(() => setIsOpen(false), [])

  const handleClear = React.useCallback(() => {
    onChange(fieldId, [])
  }, [fieldId, onChange])

  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <div className="pointer tw:flex tw:items-center full-height brada4">
          <PopoverTrigger
            render={
              <Button
                variant="default"
                className="brada8 medium px8 mr8 tw:flex-1"
              />
            }
          >
            {title}
          </PopoverTrigger>
        </div>
        <PopoverContent
          backdrop
          side="bottom"
          align="start"
          className="tw:w-auto tw:overflow-hidden tw:p-0"
        >
          <RestrictionEditor
            workflowTypes={actualTypes.map(type => type.type)}
            onChange={handleChange}
            onClose={close}
          />
        </PopoverContent>
      </Popover>
      {!!actualTypes.length && (
        <div className="tw:flex tw:items-center">
          <Trash2Icon
            size={ICON_SIZE}
            className="tw:shrink-0 tw:cursor-pointer tw:text-muted-foreground"
            onClick={handleClear}
          />
        </div>
      )}
    </>
  )
}

export default inject('workflowTypesStore')(observer(RestrictionButton))
